"use client";

import { useEffect, useState, Suspense, lazy, useRef } from "react";
import { motion, useMotionValue } from "framer-motion";
import { useInView } from "react-intersection-observer";

const Spline = lazy(() => import("@splinetool/react-spline"));

/* ===============================
   PROPS DEL HERO (TEXTOS DINÁMICOS)
=============================== */
type HeroProps = {
  badge: string;
  title: string;
  subtitle: string;
};

export default function Hero({ badge, title, subtitle }: HeroProps) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const heroWrapper = useRef<HTMLDivElement | null>(null);
  const [loadRobot, setLoadRobot] = useState(false);

  const { ref: heroRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView) setLoadRobot(true);
  }, [inView]);

  // ⭐ Seguimiento del cursor (NO TOCAR)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!inView || !heroWrapper.current) return;

      const rect = heroWrapper.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      x.set(mouseX);
      y.set(mouseY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [inView, x, y]);

  return (
    <section
      id="hero"
      ref={(el) => {
        heroRef(el);
        heroWrapper.current = el;
      }}
      className="
        relative bg-black
        min-h-[85vh]
        md:min-h-screen
        flex items-center
        overflow-hidden
        z-[0]
      "
    >
      {/* MOBILE */}
      <div className="md:hidden absolute inset-0 z-[5] flex items-center justify-center bg-black">
        <img
          src="/robot_mobile.png"
          alt="Robot Sergen"
          className="
            w-full
            h-full
            object-contain
            scale-[1.50]
            translate-y-[55px]
            translate-x-[15px]
          "
        />
      </div>

      {/* HALO */}
      {inView && (
        <motion.div className="hidden md:block absolute inset-0 pointer-events-none z-[60]">
          <motion.div
            style={{
              x,
              y,
              translateX: "-50%",
              translateY: "-50%",
            }}
            className="absolute"
          >
            {/* Núcleo */}
            <div
              style={{
                width: 130,
                height: 130,
                borderRadius: "50%",
                background: "white",
                boxShadow: "0 0 45px 20px rgba(255,255,255,0.95)",
              }}
            />

            {/* Resplandor cercano */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 450,
                height: 450,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.20) 40%, transparent 80%)",
                filter: "blur(40px)",
              }}
            />

            {/* Resplandor extendido */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 900,
                height: 900,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.10) 40%, rgba(255,255,255,0.05) 60%, transparent 100%)",
                filter: "blur(80px)",
                opacity: 0.9,
              }}
            />
          </motion.div>
        </motion.div>
      )}

      {/* TEXTO */}
      <div className="container relative z-[80] mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          <div className="lg:w-5/12 p-6 md:p-12 text-center md:text-left mt-[350px] md:mt-0">
            <span className="mb-4 inline-block rounded-full bg-sergen-500/20 px-4 py-1.5 text-sm md:text-base font-medium text-sergen-300">
              {badge}
            </span>

            <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-bold text-[#F2F2F2] leading-tight">
              {title}
            </h1>

            <p className="mb-8 text-lg md:text-xl lg:text-2xl font-semibold text-sergen-300">
              {subtitle}
            </p>
          </div>

          {/* ROBOT */}
          <div
            className="
              hidden md:flex
              lg:w-7/12
              relative
              h-[850px]
              scale-[1.15]
              translate-y-[80px]
              justify-end
              z-[20]
              overflow-visible
            "
          >
            {loadRobot ? (
              <Suspense fallback={<div className="text-white">Cargando robot...</div>}>
                <Spline scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode" />
              </Suspense>
            ) : (
              <div className="text-white opacity-50">Preparando robot…</div>
            )}
          </div>
        </div>
      </div>

      {/* FRANJA INFERIOR */}
      <div
        className="
          hidden md:block
          absolute bottom-0 left-0 w-full
          bg-[#B97400]
          h-[12px]
          z-[999]
        "
      ></div>
    </section>
  );
}
